// Priority request scheduler: serialized, priority-ordered, rate-limit aware.
// The API key is globally shared and rate-limited to ~1 req/sec, and
// speculative prefetch is bursty by design. So we run ONE request at a time
// through a priority queue: foreground moves (the node you're on) always jump
// ahead of background prefetch, and 429s back off honoring Retry-After.
// Backoff sleeps happen OUTSIDE the queue slot, so a stalled prefetch never
// blocks a foreground action.

export const PRI = { FG: 10, GROUND: 5, PREFETCH: 0 } as const;
export type Priority = number;

export const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

interface Job<T> {
  priority: Priority;
  run: () => Promise<T>;
  resolve: (v: T) => void;
  reject: (e: unknown) => void;
}

const MIN_GAP = 120;       // ms between request starts when the bucket is healthy
// Adaptive pacing: the shared budget is ~1 req/sec, so a burst at MIN_GAP
// guarantees a train of 429s (measured: 64% of calls retried at 120ms).
// After a 429 we stretch the send gap toward the real refill rate and decay
// back once the bucket has been quiet for a while.
const GAP_AFTER_429 = 950;
const GAP_RECOVERING = 400;
const HOT_WINDOW = 10_000;   // ms since last 429 → full backoff gap
const WARM_WINDOW = 30_000;  // ms since last 429 → intermediate gap

export class RequestScheduler {
  private queue: Job<any>[] = [];
  private pumping = false;
  private lastStart = 0;
  private last429 = -Infinity;
  private now: () => number;

  constructor(now: () => number = () => performance.now()) {
    this.now = now;
  }

  /** Called by the client whenever the API answers 429/503. */
  noteRateLimit(): void { this.last429 = this.now(); }

  currentGap(): number {
    const since = this.now() - this.last429;
    if (since < HOT_WINDOW) return GAP_AFTER_429;
    if (since < WARM_WINDOW) return GAP_RECOVERING;
    return MIN_GAP;
  }

  enqueue<T>(run: () => Promise<T>, priority: Priority): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.queue.push({ priority, run, resolve, reject });
      this.queue.sort((a, b) => b.priority - a.priority);
      void this.pump();
    });
  }

  private async pump(): Promise<void> {
    if (this.pumping) return;
    this.pumping = true;
    while (this.queue.length) {
      const gap = this.currentGap() - (this.now() - this.lastStart);
      if (gap > 0) await sleep(gap);
      const job = this.queue.shift()!;
      this.lastStart = this.now();
      try { job.resolve(await job.run()); }
      catch (e) { job.reject(e); }
    }
    this.pumping = false;
  }
}

export const scheduler = new RequestScheduler();
